import { FC, useState, useEffect, ChangeEvent } from 'react'
import {
  useDiamondFilterAPI,
  useDiamondFilterState,
} from 'context/diamondFilterContext'
import {
  DiamondFilterAPIContextType,
  DiamondFilterContextType,
  IDiamondFilter,
} from 'types/diamondFilter.types'
import { useRouter } from 'next/router'
import Image from 'next/image'
import images from '@publicAssets/images/ui/diamond-search/diamond-w-image-icon.svg'
import fast from '@publicAssets/images/ui/diamond-search/fast.svg'
import Tooltip from '../Tooltip'
import Information from '@components/yadav/icons/information'
import Information2 from '@components/yadav/icons/information2'
import { TooltipV2 } from '@components/yadav/common'
import OriginTooltipContent from './OriginTooltipContent'

const CheckboxesSelector = () => {
  const { diamondFilterState } =
    useDiamondFilterState() as DiamondFilterContextType
  const { onWithImgUpdate, onSameDayUpdate, onQuickShipUpdate, onFtcLinkUpdate } =
    useDiamondFilterAPI() as DiamondFilterAPIContextType
  const [isShownFast, setIsShownFast] = useState(false)
  const [isDiamondOriginChecked, setIsDiamondOriginChecked] = useState(false)
  const [TooltipVisible, setTooltipVisible] = useState(false)

  const router = useRouter()

  useEffect(() => {
    const checkStoredFilters = () => {
      try {
        const storedSearchDiamonds = window.localStorage.getItem('search-diamonds')
        const storedWithFtcLink = window.localStorage.getItem('withFtcLink')

        if (storedSearchDiamonds) {
          const state = JSON.parse(storedSearchDiamonds) as IDiamondFilter
          if (
            state.withImg === undefined ||
            state.sameDayInStorePickup === undefined ||
            state.quickShip === undefined ||
            state.withFtcLink === undefined
          ) {
            window.localStorage.removeItem('search-diamonds')
          } else {
            onWithImgUpdate(state.withImg)
            onSameDayUpdate(state.sameDayInStorePickup)
            onQuickShipUpdate(state.quickShip)
            setIsDiamondOriginChecked(state.withFtcLink)
            onFtcLinkUpdate(state.withFtcLink)
          }
        }

        if (storedWithFtcLink === 'true') {
          setIsDiamondOriginChecked(true)
          onFtcLinkUpdate(true)
          window.localStorage.removeItem('withFtcLink')
        }
      } catch (error) {
        window.localStorage.removeItem('search-diamonds')
      }
    }

    const handleRouteChange = () => {
      checkStoredFilters()

      if (router.pathname.includes('/natural-loose-diamonds') || router.pathname.includes('/lab-created-diamonds')) {
        window.localStorage.setItem('withFtcLink', 'false')
        setIsDiamondOriginChecked(false)
        onFtcLinkUpdate(false)
      }

      const forceUpdate = window.localStorage.getItem('forceUpdate')
      if (forceUpdate === 'true') {
        setIsDiamondOriginChecked(true)
        onFtcLinkUpdate(true)
        window.localStorage.removeItem('forceUpdate')
      }
    }

    checkStoredFilters()

    router.events.on('routeChangeComplete', handleRouteChange)

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange)
    }
  }, [router, onWithImgUpdate, onSameDayUpdate, onQuickShipUpdate, onFtcLinkUpdate])

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!TooltipVisible) {
      switch (e.target.name) {
        case 'withImg':
          onWithImgUpdate(e.target.checked)
          break
        case 'sameDay':
          onSameDayUpdate(e.target.checked)
          break
        case 'fast':
          onQuickShipUpdate(e.target.checked)
          break
        case 'diamondOrigin':
          setIsDiamondOriginChecked(e.target.checked)
          onFtcLinkUpdate(e.target.checked)
          break
        default:
          break
      }
    }
  }

  return (
    <div className="flex flex-row gap-1">

      {diamondFilterState.labCreated === false && (
        <div
          className="w-auto border border-accent-1 rounded-full px-4 py-1 flex flex-row items-center hover:cursor-pointer bg-[#6E868A]"
        >

          <input
            type="checkbox"
            id="diamondOrigin"
            name="diamondOrigin"
            className="mr-2 cursor-pointer flex flex-row items-center focus:ring focus:ring-primary text-accent-9 hover:cursor-pointer border-2 border-white checked:border-white hover:checked:border-white"
            onChange={(e) => {
              handleOnChange(e)
            }}
            checked={isDiamondOriginChecked}
          />
          <label
            htmlFor="diamondOrigin"
            className="flex items-center text-white text-sm font-normal hover:cursor-pointer"
          >

            <span>Diamond Origin</span>
            <div
              onClick={(e) => e.stopPropagation()}
              className="ml-2 flex items-center justify-center"
              onMouseEnter={() => setTooltipVisible(true)}
              onMouseLeave={() => setTooltipVisible(false)}
            >
              <TooltipV2
                content={<OriginTooltipContent />}
                className="bg-white shadow-lg rounded-md p-2 border border-gray-300 right-0 sm:right-auto mx-[9px] sm:mx-0 mt-[12px]"
                closeButtonPosition={true}
                onClose={() => setTooltipVisible(false)}
              >
                {isDiamondOriginChecked ? <Information /> : <Information />}
              </TooltipV2>
            </div>
          </label>
        </div>
      )}


      <div className="w-auto border border-accent-1 rounded-full px-4 py-1 flex-row items-center hover:cursor-pointer hidden md:flex">
        <input
          type="checkbox"
          id="withImg"
          name="withImg"
          className="mr-2 cursor-pointer flex flex-row items-center focus:ring focus:ring-primary text-accent-9 hover:cursor-pointer"
          onChange={(e) => {
            handleOnChange(e)
          }}
          checked={diamondFilterState.withImg}
        />
        <label
          htmlFor="withImg"
          className="flex flex-row items-center text-gray-dark text-sm font-normal hover:cursor-pointer"
        >
          <span className="mx-1">Real View</span>
          <Image src={images} alt="images" width={17} height={17} />
        </label>
      </div>
      {/* The below checkbox won't be needed now. The lines were commented on in case we need the checkbox in the future. More info --> https://tracker.serfe.com/view.php?id=106110 */}
      {/* <div className="w-auto border border-accent-1 rounded-full px-4 py-1 flex flex-row items-center">
        <div
          onMouseEnter={() => setIsShownSameDay(true)}
          onMouseLeave={() => setIsShownSameDay(false)}
        >
          <input
            type="checkbox"
            id="sameDay"
            name="sameDay"
            className="hidden"
            onChange={handleOnChange}
            checked={diamondFilterState.sameDayInStorePickup}
          />
          <label htmlFor="sameDay" className={s.labelRB}>
            <span className="mx-1 relative">Same Day In-Store Pickup</span>
            <div className="hidden lg:block absolute mt-3">
              {isShownSameDay && (
                <Tooltip
                  title="Ready for in-store pickup. Delivery in 1 - 2 business days for loose diamonds only."
                  image={
                    <Image
                      src={sameDayIcon}
                      height={25}
                      width={25}
                      className="align-middle"
                      alt="readyImg"
                    />
                  }
                  className="h-[95px] w-[400px]"
                  activeHover={true}
                >
                  {''}
                </Tooltip>
              )}
            </div>
            <Image src={sameDayIcon} alt="sameDay" width={24} height={20.8} />
          </label>
        </div>
      </div> */}
      {/* The below checkbox won't be needed now. The lines were commented on in case we need the checkbox in the future. More info --> https://tracker.serfe.com/view.php?id=113394 */}
      {/* <div className="w-auto border border-accent-1 rounded-full px-4 py-1 flex flex-row items-center hover:cursor-pointer">
        <div
          onMouseEnter={() => setIsShownFast(true)}
          onMouseLeave={() => setIsShownFast(false)}
          className="flex flex-row items-center"
        >
          <input
            type="checkbox"
            id="fast"
            name="fast"
            className="mr-2 cursor-pointer focus:ring focus:ring-primary text-accent-9"
            onChange={handleOnChange}
            checked={diamondFilterState.quickShip}
          />
          <label
            htmlFor="fast"
            className="flex flex-row items-center text-gray-dark text-sm font-normal hover:cursor-pointer"
          >
            <span className="mx-1 relative">Fast Shipping</span>
            <div className="hidden lg:block absolute mt-3">
              {isShownFast && (
                <Tooltip
                  title="Delivery in 2 - 4 business days for loose diamonds only."
                  image={
                    <Image
                      src={fast}
                      height={20}
                      width={20}
                      className="align-middle"
                      alt="deliveryImg"
                    />
                  }
                  activeHover={true}
                  className="h-[55px] w-[400px]"
                >
                  {''}
                </Tooltip>
              )}
            </div>

            <Image src={fast} alt="fast" width={24} height={20.8} />
          </label>
        </div>
      </div> */}
    </div>
  )
}

export default CheckboxesSelector
